import { useState, type ReactNode } from "react";
import type { Badge, Candidate, Judgement, Pair } from "./types";
import { badgeLabel, worseBadge } from "./jev";

export function formatMs(ms: number | undefined): string {
  if (ms === undefined) return "—";
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(2)}s`;
}

function snippet(text: string, max: number): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  return normalized.length > max ? `${normalized.slice(0, max)}…` : normalized;
}

export function KindChip({ kind }: { kind: Candidate["kind"] }) {
  return (
    <span className={`kind kind-${kind}`} aria-label={kind === "statute" ? "법조문" : "판례"}>
      {kind === "statute" ? "법" : "판"}
    </span>
  );
}

export function BadgePill({ badge, pending = true }: { badge: Badge | null; pending?: boolean }) {
  if (badge === null) {
    if (!pending) {
      return (
        <span className="badge badge-none" aria-label="판정 없음">
          <em>판정 없음</em>
        </span>
      );
    }
    return (
      <span className="badge badge-judging" aria-label="판정 중">
        <span className="dot" />
        <span className="dot" />
        <span className="dot" />
      </span>
    );
  }
  return (
    <span className={`badge badge-${badge}`}>
      <b>{badge === 1 ? "+1" : badge === 0 ? "0" : "−1"}</b>
      <em>{badgeLabel(badge)}</em>
    </span>
  );
}

function ProbRow({ name, value, tone }: { name: string; value: number; tone: string }) {
  return (
    <div className="prob-row">
      <span className="prob-name">{name}</span>
      <span className="prob-track">
        <span className={`prob-fill tone-${tone}`} style={{ width: `${Math.max(1, value * 100)}%` }} />
      </span>
      <span className="prob-value">{(value * 100).toFixed(0)}%</span>
    </div>
  );
}

export function Probs({
  judgement,
  pending = true,
}: {
  judgement: Judgement | undefined;
  pending?: boolean;
}) {
  if (!judgement) {
    return (
      <div className="probs probs-empty">
        <span>{pending ? "판정 대기 중" : "판정 없음"}</span>
      </div>
    );
  }
  return (
    <div className="probs">
      <ProbRow name="인용" value={judgement.pPass} tone="pass" />
      <ProbRow name="보류" value={judgement.pHold} tone="hold" />
      <ProbRow name="제외" value={judgement.pDrop} tone="drop" />
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [state, setState] = useState<"idle" | "done" | "fail">("idle");
  return (
    <button
      type="button"
      className="copy"
      onClick={() => {
        void navigator.clipboard
          .writeText(text)
          .then(() => {
            setState("done");
            setTimeout(() => setState("idle"), 1400);
          })
          .catch(() => {
            setState("fail");
            setTimeout(() => setState("idle"), 1800);
          });
      }}
    >
      {state === "done" ? "복사됨" : state === "fail" ? "복사 실패" : "원문 복사"}
    </button>
  );
}

function ExpandedBody({
  candidate,
  judgement,
  pending,
}: {
  candidate: Candidate;
  judgement?: Judgement;
  pending?: boolean;
}) {
  return (
    <div className="expanded">
      <div className="expanded-meta">
        <span>{candidate.label}</span>
        {candidate.meta ? <span className="muted">{candidate.meta}</span> : null}
      </div>
      <p className="original">{candidate.text}</p>
      <Probs judgement={judgement} pending={pending} />
      <div className="expanded-actions">
        <CopyButton text={`${candidate.label}\n${candidate.text}`} />
        <span className="hint">자동 제출되지 않습니다</span>
      </div>
    </div>
  );
}

export function CandidateCard({
  candidate,
  judgement,
  expanded,
  onToggle,
  pending,
}: {
  candidate: Candidate;
  judgement?: Judgement;
  expanded: boolean;
  onToggle: () => void;
  pending?: boolean;
}) {
  return (
    <article className={`card${expanded ? " is-open" : ""}`}>
      <button type="button" className="card-head" onClick={onToggle} aria-expanded={expanded}>
        <KindChip kind={candidate.kind} />
        <span className="card-main">
          <span className="card-label">{candidate.label}</span>
          <span className="card-snippet">{snippet(candidate.text, 78)}</span>
        </span>
        <BadgePill badge={judgement?.badge ?? null} pending={pending} />
      </button>
      {expanded ? (
        <ExpandedBody candidate={candidate} judgement={judgement} pending={pending} />
      ) : null}
    </article>
  );
}

export function PairCard({
  pair,
  judgements,
  expanded,
  onToggle,
  pending,
}: {
  pair: Pair;
  judgements: Record<string, Judgement>;
  expanded: boolean;
  onToggle: () => void;
  pending?: boolean;
}) {
  const sj = judgements[pair.statute.id];
  const cj = judgements[pair.case.id];
  const composite = worseBadge(sj?.badge ?? null, cj?.badge ?? null);

  return (
    <article className={`pair${expanded ? " is-open" : ""}`} data-composite={composite ?? ""}>
      <button type="button" className="pair-head" onClick={onToggle} aria-expanded={expanded}>
        <span className="pair-main">
          <span className="pair-row">
            <KindChip kind="statute" />
            <span className="pair-text">
              <span className="card-label">{pair.statute.label}</span>
              <span className="card-snippet">{snippet(pair.statute.text, 46)}</span>
            </span>
            <BadgePill badge={sj?.badge ?? null} pending={pending} />
          </span>
          <span className="pair-link" aria-hidden="true">
            ↔
          </span>
          <span className="pair-row">
            <KindChip kind="case" />
            <span className="pair-text">
              <span className="card-label">{pair.case.label}</span>
              <span className="card-snippet">{snippet(pair.case.text.replace(/^【이유】\s*/, ""), 46)}</span>
            </span>
            <BadgePill badge={cj?.badge ?? null} pending={pending} />
          </span>
        </span>
      </button>
      {expanded ? (
        <div className="pair-body">
          <ExpandedBody candidate={pair.statute} judgement={sj} pending={pending} />
          <ExpandedBody candidate={pair.case} judgement={cj} pending={pending} />
        </div>
      ) : null}
    </article>
  );
}

export function Slot({
  title,
  sigil,
  count,
  className,
  children,
}: {
  title: string;
  sigil: string;
  count: number;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`slot ${className ?? ""}`}>
      <header className="slot-head">
        <span className="slot-title">
          <span className="sigil" aria-hidden="true">
            {sigil}
          </span>
          {title}
        </span>
        <span className="slot-count">{count}건</span>
      </header>
      <div className="slot-body">{children}</div>
    </section>
  );
}

export function SkeletonCard() {
  return (
    <div className="skel" aria-hidden="true">
      <span className="skel-line w40" />
      <span className="skel-line w90" />
      <span className="skel-line w70" />
    </div>
  );
}

export function SkeletonSlot({ rows = 3 }: { rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <SkeletonCard key={i} />
      ))}
    </>
  );
}

export function EmptyState({ examples, onPick }: { examples: string[]; onPick: (q: string) => void }) {
  return (
    <div className="empty">
      <p className="empty-title">분쟁 사실관계를 그대로 적어보세요.</p>
      <p className="empty-sub">조문과 판례를 함께 찾아 인용·보류·제외로 판정합니다.</p>
      <ul className="examples">
        {examples.map((q) => (
          <li key={q}>
            <button type="button" onClick={() => onPick(q)}>
              {q}
              <span aria-hidden="true">→</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
